import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Modal, Paper, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Assuming feeConfig has seller-specific fees
import { feeConfig } from '../../../utils/constants/fees';
import { useSelector } from 'react-redux';
import { reducerPaths } from '../../../app/reducerPaths';
import { USER_ROLES } from '../../../utils/constants/userRoles';
import CommaSeparatedInput from '../CustomInputs/CommaSeparatedInput';
import { calculateBuyerFee, calculateTaxForCostBreakdown } from '../../../utils/methods/generalVehicleMethods';

// --- Fee Calculation Logic ---
/**
 * Calculates the seller fee based on the tiered structure defined in feeConfig.
 * @param {number} price The vehicle purchase price (bid amount).
 * @returns {{amount: number, description: string}} The calculated fee and its description.
 */
const calculateSellerFee = (price) => {
  if (price <= 0 || isNaN(price)) {
    return { amount: 0, description: '0%' };
  }

  // Find the matching fee tier
  const tier = feeConfig.seller.find(t => {
    // Check min boundary
    if (price < t.min) return false;

    // Check max boundary (handles both fixed max and null max for the final tier)
    if (t.max !== null && price > t.max) return false;

    return true;
  });

  if (!tier) {
    // Fallback for edge cases outside the defined tiers
    return { amount: 0, description: 'N/A' };
  }

  if (tier.fee) {
    // Fixed fee tier
    return { 
      amount: tier.fee, 
      description: `` 
    };
  } else if (tier.fee_percent) {
    // Percentage fee tier
    const calculatedFee = price * (tier.fee_percent / 100);
    return { 
      amount: calculatedFee, 
      description: `${tier.fee_percent}% of Price` 
    };
  }
  
  return { amount: 0, description: 'N/A' };
};


// --- Helper Component for a single cost item row (Kept the same) ---

const CostRow = ({ label, amount, isTotal = false, isHeader = false }) => (
  <Box
    component="div"
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      // All units in rem
      py: isHeader ? '0.5rem' : '0.75rem',
      borderBottom: isTotal ? '0.125rem solid' : '0.0625rem solid',
      borderColor: isTotal ? 'primary.main' : 'grey.300',
      fontWeight: isTotal ? 700 : (isHeader ? 600 : 400),
      fontSize: isTotal ? '1.1rem' : '1rem',
      color: isTotal ? 'primary.main' : 'text.primary',
      '&:last-of-type': {
        borderBottom: isTotal ? '0.125rem solid' : 'none',
      }
    }}
  >
    <Typography
      variant="body1"
      component="span"
      sx={{
        fontWeight: isTotal ? 700 : (isHeader ? 600 : 400),
        fontSize: 'inherit',
        color: isHeader ? 'text.secondary' : 'inherit',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      component="span"
      sx={{
        fontWeight: isTotal ? 700 : (isHeader ? 600 : 400),
        fontSize: 'inherit',
        color: 'inherit',
      }}
    >
      {/* Formats amount to currency string: $XX,XXX.XX */}
      {amount !== null ? `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}` : 'N/A'}
    </Typography>
  </Box>
);

// --- Internal Component: Content for the Modal (The breakdown logic) ---

const SellerFeesContent = ({ car, purchasePrice, setPurchasePrice }) => {
  const { t } = useTranslation('liveAuction');
  const auth = useSelector(store => store[reducerPaths.auth]);

  // Use the internal state value (purchasePrice) for calculations
  const effectivePrice = parseFloat(purchasePrice) || 0; 
  
  // Calculate Seller Fee using the tiered logic
  const { amount: sellerFee, description: sellerFeeDescription } = useMemo(() => {
    return calculateSellerFee(effectivePrice);
  }, [effectivePrice]);
  
  // Use the actual service fee from feeConfig
  const serviceFeeFixed = feeConfig.service_fee || 98.00; 

  const subtotal = useMemo(() =>
    effectivePrice - sellerFee - serviceFeeFixed,
    [effectivePrice, sellerFee, serviceFeeFixed]
  );

  const salesTax = calculateTaxForCostBreakdown(
    effectivePrice,
    calculateBuyerFee(effectivePrice), // buyerFee
    serviceFeeFixed
  );

  // Total of the fees being displayed
  const totalFees = useMemo(() => {
    let total = subtotal;

    if (auth?.user_role === USER_ROLES.dealer && car?.Seller_Id === auth?.user_id && salesTax) {
      total += salesTax;
    }
    
    return total;
  }, [sellerFee, serviceFeeFixed, effectivePrice, salesTax]);

  // Data for Rendering - Now includes the dynamic price
  const breakdownItems = [
    { label: t('Vehicle Purchase Amount'), amount: effectivePrice },
    { label: t('Seller Fee'), amount: sellerFee },
    { label: t('Service Fee'), amount: serviceFeeFixed },
  ];

  if (auth?.user_role === USER_ROLES.dealer && car?.Seller_Id === auth?.user_id) {
    breakdownItems.push(
      { label: t('Sales Tax'), amount: salesTax },
    )
  }
  
  const handlePriceChange = (event) => {
    // Allow only numerical input (or empty string)
    if (parseInt(event?.target?.value) <= 10 ** 9) {
      setPurchasePrice(event.target.value);
    }
  };


  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        p: '1.5rem',
        pt: "0",
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* --- HEADER --- */}
      <Typography
        variant="h6"
        component="h2"
        id="seller-fees-modal-title"
        sx={{
          fontSize: '1.1rem',
          fontWeight: 700,
          textAlign: "center",
          color: 'text.primary',
          borderBottom: '0.0625rem solid',
          borderColor: 'grey.300',
          py: '1rem',
        }}
      >
        {t('Cost breakdown')}
      </Typography>
      
      {/* --- INPUT FIELD: Vehicle Purchase Amount --- */}
      <CommaSeparatedInput
        fullWidth
        label={t("Vehicle Purchase Amount")}
        variant="outlined"
        value={purchasePrice}
        onChange={handlePriceChange}
        margin="normal"
        type="text"
        placeholder={t("e.g., 25000")}
        inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
        sx={{ mb: '1.5rem' }}
      />

      {/* --- BREAKDOWN ITEMS --- */}
      {purchasePrice > 0 && (
        <Box component="section" sx={{ mb: '1rem' }} id="seller-fees-modal-description">
          {breakdownItems.map((item, index) => (
            <CostRow key={index} label={item.label} amount={item.amount} />
          ))}
        </Box>)
      }

      {/* --- TOTAL ESTIMATED FEES --- */}
      {purchasePrice > 0 && (
        <CostRow
          label={t("Total price")}
          amount={totalFees}
          isTotal={true}
        />)
      }

      {/* Show a placeholder if bid is zero */}
      {purchasePrice <= 0 && (
        <Typography 
          variant="body2" 
          color="text.primary" 
          textAlign="center" 
          sx={{ py: 1 }}
        >
          {t('Enter a purchase amount above $0 to see the full breakdown.')}
        </Typography>
      )}
    </Box>
  );
};


// --- Main Exported Component: Button and Modal Wrapper ---

function CostBreakdownSeller({
  car
}) {
  
  const { t } = useTranslation('liveAuction');

  const [open, setOpen] = useState(false);
  // State for the input field is managed here
  const [purchasePrice, setPurchasePrice] = useState(''); 
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Styles for the modal box positioning
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // width: { xs: '90%' }, // Responsive width
    maxWidth: "700px",
    bgcolor: 'background.paper',
    borderRadius: '0.5rem',
    boxShadow: 24,
    maxHeight: '80vh',
    overflowY: 'auto',
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center"
      }}
    >
      {/* 1. Button to show the calculator */}
      <Button 
        variant="contained" 
        onClick={handleOpen} 
        sx={{ 
          textTransform: 'none', 
          fontWeight: 600,
        }}
      >
        {t('Cost breakdown')}
      </Button>

      {/* 2. Modal containing the fees breakdown */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="seller-fees-modal-title"
        aria-describedby="seller-fees-modal-description"
      >
        {/* Use Paper for a nice, elevated look inside the Modal */}
        <Paper sx={modalStyle}>
          {/* Pass the state and setter down to the content component */}
          <SellerFeesContent 
            car={car}
            purchasePrice={purchasePrice} 
            setPurchasePrice={setPurchasePrice}
          /> 
        </Paper>
      </Modal>
    </Box>
  );
}

export default CostBreakdownSeller;